import { signInWithPassword } from "@src/appV2/Auth/cognito/cognitoPasswordAuth";
import { exchangeCognitoTokenForFirebase } from "@src/appV2/Auth/cognito/exchangeCognitoTokenForFirebase";
import { isFirebaseSessionFallbackEnabled } from "@src/appV2/Auth/cognito/firebaseSessionFallbackState";
import { mapCognitoError } from "@src/appV2/Auth/cognito/mapCognitoError";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { FirebaseAuth } from "@src/utils/firebase";

interface LoginWithEmailAndPasswordParams {
  email: string;
  password: string;
}

/**
 * Handles Cognito email/password authentication for mobile.
 * Mirrors the magic link flow in AuthRedirect: Cognito-first,
 * with optional Firebase token exchange for backward compatibility.
 */
export async function loginWithEmailAndPassword({
  email,
  password,
}: LoginWithEmailAndPasswordParams): Promise<
  { success: true } | { success: false; error: string }
> {
  try {
    const credentials = await signInWithPassword(email, password);

    if (isFirebaseSessionFallbackEnabled()) {
      const firebaseCustomToken = await exchangeCognitoTokenForFirebase(credentials.idToken);
      await FirebaseAuth.signInWithCustomToken(firebaseCustomToken);
    }

    logEvent(APP_V2_APP_EVENTS.PASSWORD_LOGIN_COGNITO_SESSION_SUCCESS);

    return { success: true };
  } catch (error: unknown) {
    const mapped = mapCognitoError(error, "password");

    logError(APP_V2_APP_EVENTS.PASSWORD_LOGIN_COGNITO_SESSION_FAILURE, {
      error,
      metadata: { errorCode: mapped.code },
    });

    return { success: false, error: mapped.message };
  }
}
